// fomService will be refactored and probably moved so we dont need to fix eslint error

/* eslint-disable no-empty */
import {
  getLocalData,
  clearLocalData,
  saveLocalData,
  WebPlayActionCardService,
  getHighchartAsPng,
} from '@playbooks/web-play-component'
import { getActionCardDataReplacementInfo } from '@playbooks/web-play-component/action-card-notification'
import { ActionCardModel } from '@playbooks/action-cards-utils'

import { FomToken } from './FomToken'

const FlowStatuses: Record<string, number> = {
  play: 1540,
  pause: 1541,
  stop: 1542,
  complete: 1545,
}

const FOM_SERVICE_URL = import.meta.env.VITE_FOM_SERVICE_URL

const fomService = {
  async getWebPlayInfo(playbookId: string | null) {
    const response = await fetch(`${FOM_SERVICE_URL}/getWebPlayInfo?playbookId=${playbookId}`)
    if (!response.ok) {
      return { statusCode: response.status, webPlayInfo: null }
    }

    const info = await response.json()
    // info can have status code if a user doesn't have access
    // If we have webPlayInfo in the response we set status code to 200 we self
    // because we have logic depended on status code
    const statusCode = info.statusCode || 200
    return {
      statusCode,
      webPlayInfo: info,
    }
  },

  async createPlay(
    playbookId: string,
    title: string,
    keywords: string[],
    score: number,
    scoringDimensions: unknown[],
    lastActiveSeparator: string | undefined,
    flow: string,
    loggedInUserId: string,
  ) {
    const url = new URL(`${FOM_SERVICE_URL}/createPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('title', title)
    url.searchParams.append('keywords', keywords.join(','))
    url.searchParams.append('score', String(score))
    url.searchParams.append('lastActiveSeparator', lastActiveSeparator || '')
    url.searchParams.append('flow', String(FlowStatuses[flow] || FlowStatuses.play))
    url.searchParams.append('loggedInUserId', loggedInUserId)

    const formData = new FormData()
    try {
      const scoringDimensionsString = scoringDimensions?.length > 0 ? JSON.stringify(scoringDimensions) : null
      if (scoringDimensionsString) {
        formData.append('scoringDimensions', scoringDimensionsString)
      }
    } catch {}

    const playCreationResponse = await fetch(url, {
      method: 'POST',
      body: formData,
    })
    if (playCreationResponse.status !== 200) {
      throw new Error('Failed to create play: ' + playCreationResponse.statusText)
    }
    const fomToken = new FomToken(playCreationResponse)
    const playId = await playCreationResponse.text()

    return { playId, fomToken }
  },

  async attachFileToPlay(
    playbookId: string,
    playId: string,
    headerId: string,
    headerName: string,
    file: File,
    fomToken: unknown,
  ): Promise<[number, string]> {
    if (file.size > 30 * 1024 * 1024) {
      throw new Error('File size exceeds the limit of 30MB')
    }

    const url = new URL(`${FOM_SERVICE_URL}/attachFileToPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('playId', playId)
    url.searchParams.append('headerId', headerId)
    url.searchParams.append('headerName', headerName)

    const formData = new FormData()
    formData.append('file', file)

    const headers = (fomToken as FomToken).appendToObject({})

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: headers,
    })

    if (!response.ok) {
      console.error('Failed to attach file to project file:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }

    const fileData = await response.json()
    return [fileData.id, fileData.url]
  },

  async attachActionCardToPlay(playbookId: string, playId: string, actionCard: unknown, fomToken: unknown) {
    const url = new URL(`${FOM_SERVICE_URL}/attachActionCardToPlay`)
    url.searchParams.append('playbookId', playbookId.toString())
    url.searchParams.append('playId', playId.toString())

    const headers = (fomToken as FomToken).appendToObject({})
    headers['Content-Type'] = 'application/json'

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(actionCard),
    })

    if (!response.ok) {
      console.error('Failed to attach action card to play:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }
  },

  async sendPlayCreatedNotification(
    playbookId: string,
    playId: string,
    email: string,
    keywords: string[],
    actionCardModel: ActionCardModel,
    fomToken: unknown,
  ) {
    const url = new URL(`${FOM_SERVICE_URL}/sendPlayCreatedNotification`)
    url.searchParams.append('playbookId', playbookId.toString())
    url.searchParams.append('playId', playId.toString())

    const headers = (fomToken as FomToken).appendToObject({})

    const formData = new FormData()
    formData.append('email', email)
    formData.append('keywords', keywords.join(','))
    formData.append('actionCardData', getActionCardDataReplacementInfo(actionCardModel))

    const pngBlob = await getHighchartAsPng(actionCardModel.data)
    if (pngBlob) {
      formData.append('scoringDimensionsChart', pngBlob as Blob)
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: formData,
    })

    if (!response.ok) {
      console.error('Failed to attach action card to play:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }
  },

  async createAnonymousToken() {
    const response = await fetch(`${FOM_SERVICE_URL}/createAnonymousToken`)
    return await response.text()
  },

  async getActionCard({
    playbookId,
    actionCardUrl,
    headersCount,
    email,
  }: {
    playbookId: string | null
    actionCardUrl: string
    headersCount: number
    email: string | null
  }) {
    const localData = await getLocalData(playbookId!)

    if (localData) {
      return localData
    }
    if (actionCardUrl) {
      const response = await fetch(actionCardUrl)
      const data = await response.json()

      const actionCardService = new WebPlayActionCardService(data)

      actionCardService.adjustEmail(email)
      actionCardService.adjustModelFromQueryString(new URLSearchParams(window.location.search))

      if (headersCount === 1 && data.sections.length && data.sections[0].type === 'Separator') {
        // expand single separator if it comes first as it will be hidden
        data.sections[0].isOpened = true
      }

      await clearLocalData(playbookId!)
      await saveLocalData(data, playbookId!)
      return data
    } else {
      throw new Error('No action card found for playbookId: ' + playbookId)
    }
  },

  async getPlaybookId(code: string) {
    const infoResponse = await fetch(`${FOM_SERVICE_URL}/getWebPlayIdByCode?code=${code}`)
    return await infoResponse.json()
  },

  async rollbackPlayCreation(playbookId: string, playId: string, fomToken: unknown) {
    try {
      const url = new URL(`${FOM_SERVICE_URL}/RollbackPlayCreation`)
      url.searchParams.append('playbookId', playbookId)
      url.searchParams.append('playId', playId)

      const headers = (fomToken as FomToken).appendToObject({})

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
      })

      if (!response.ok) {
        console.error('Failed to rollback play creation:', response.statusText)
      }
    } catch (ex) {
      console.error('Failed to rollback play creation', ex)
    }
  },
}

export default fomService
